// MessageBubble Component
// Based on documentation from docs/03_CHAT_MESSAGING_SYSTEM.md

import React, { useState, useEffect } from 'react';
import { getCurrentTime, initialsFrom, formatFileSize, getFileExt } from '../utils/helpers';
import { IMAGE_TYPE, VIDEO_TYPE } from '../utils/constants';
import '../styles/MessageBubble.css';

const MessageBubble = ({ message, currentUser, users, onMediaClick }) => {
  const isOwnMessage = message.user?.peerId === currentUser?.peerId;
  // Get latest user data from users array for updated name
  const latestUserData = users.find(u => u.peerId === message.user?.peerId);
  const sender = latestUserData || message.user || {};
  
  const formatMessageText = (text) => {
    if (!text) return '';
    
    // Replace emojis with larger display
    const emojiRegex = /(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu;
    const hasOnlyEmojis = text.match(emojiRegex)?.join('').length === text.length;
    
    if (hasOnlyEmojis) {
      return <span className="emoji-only">{text}</span>;
    }
    
    return text;
  };

  const formatElapsedTime = (seconds) => {
    if (seconds < 60) return `00:${String(Math.floor(seconds)).padStart(2, '0')}`;
    if (seconds < 3600) return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
    return `${String(Math.floor(seconds / 3600)).padStart(2, '0')}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  };

  const formatSpeed = (bytesPerSec) => {
    if (bytesPerSec < 1024) return `${Math.round(bytesPerSec)} B/s`;
    if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(1)} KB/s`;
    return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`;
  };

  const formatEta = (seconds) => {
    if (seconds <= 0 || !isFinite(seconds)) return '';
    if (seconds < 60) return `~${Math.round(seconds)}s remaining`;
    return `~${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s remaining`;
  };

  const renderLinkPreview = (linkData) => {
    if (!linkData) return null;
    
    return (
      <div className="link-preview">
        {linkData.image && (
          <img src={linkData.image} alt={linkData.title || 'Link preview'} />
        )}
        <div className="link-info">
          <h4>{linkData.title || 'No title'}</h4>
          <p>{linkData.description || 'No description'}</p>
          <a href={linkData.url} target="_blank" rel="noopener noreferrer">
            {linkData.url}
          </a>
        </div>
      </div>
    );
  };

  // Live timer sub-component for active transfers
  const TransferProgressBubble = ({ fileData, progress, status, ext, getFileIcon, senderName }) => {
    const [elapsed, setElapsed] = useState(0);
    const startTime = fileData.startTime || Date.now();
    const speed = fileData.speed || 0;
    const etaSeconds = fileData.etaSeconds || 0;

    useEffect(() => {
      if (status !== 'sending' && status !== 'receiving') return;
      const interval = setInterval(() => {
        setElapsed((Date.now() - startTime) / 1000);
      }, 1000);
      return () => clearInterval(interval);
    }, [status, startTime]);

    const statusLabel = status === 'sending' 
      ? `Sending to ${senderName || 'peer'}...` 
      : `Receiving from ${senderName || 'peer'}...`;

    return (
      <div className="file-message file-transferring">
        <div className="file-thumbnail">
          <div className="file-icon">
            <img src={getFileIcon(ext)} alt={ext} style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
          </div>
          <div className="file-progress-overlay">
            <div className="file-progress-ring">
              <svg viewBox="0 0 36 36">
                <path
                  className="file-progress-ring-bg"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="file-progress-ring-fill file-progress-ring-active"
                  strokeDasharray={`${progress}, 100`}
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <span className="file-progress-percent">{Math.round(progress)}%</span>
            </div>
          </div>
        </div>
        <div className="file-info">
          <div className="file-name">{fileData.name}</div>
          <div className="file-size">{formatFileSize(fileData.size || 0)}</div>
          <div className="file-progress-bar-container">
            <div className="file-progress-bar-track">
              <div className="file-progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="file-status">{statusLabel} {Math.round(progress)}%</span>
          </div>
          <div className="file-transfer-meta">
            <span className="file-transfer-timer">⏱ {formatElapsedTime(elapsed)}</span>
            {speed > 0 && <span className="file-transfer-speed">{formatSpeed(speed)}</span>}
            {etaSeconds > 0 && etaSeconds < 3600 && <span className="file-transfer-eta">{formatEta(etaSeconds)}</span>}
          </div>
        </div>
      </div>
    );
  };

  // Render file message with thumbnail and progress
  const renderFileMessage = (fileData) => {
    const ext = getFileExt(fileData.name || '');
    const isImage = IMAGE_TYPE.includes(ext);
    const isVideo = VIDEO_TYPE.includes(ext);
    const progress = fileData.progress || fileData.percent || 0;
    const status = fileData.status || 'completed';
    const isTransferring = status === 'sending' || status === 'receiving';
    
    // Get proper file icon path
    const getFileIcon = (extension) => {
      const iconMap = {
        'pdf': 'pdf', 'doc': 'doc', 'docx': 'docx', 'xls': 'xls', 'xlsx': 'xlsx',
        'ppt': 'ppt', 'pptx': 'pptx', 'txt': 'txt', 'zip': 'zip', 'rar': 'rar',
        'csv': 'csv', 'json': 'json', 'xml': 'xml', 'html': 'html', 'css': 'css',
        'js': 'js', 'py': 'py', 'java': 'java', 'php': 'php', 'exe': 'exe',
        'mp3': 'mp3', 'wav': 'wav', 'mp4': 'mp4', 'avi': 'avi', 'mkv': 'mkv',
        'mov': 'mov', 'wmv': 'wmv', '7z': '7z', 'tar': 'tar', 'gz': 'gz', 'iso': 'iso'
      };
      return `/images/logo/${iconMap[extension] || 'file'}.png`;
    };

    // During transfer: show compact progress bubble with live timer
    if (isTransferring) {
      return (
        <TransferProgressBubble 
          fileData={fileData} 
          progress={progress} 
          status={status} 
          ext={ext} 
          getFileIcon={getFileIcon}
          senderName={sender.peerName || sender.userName}
        />
      );
    }
    
    // Completed: show full file bubble with preview
    return (
      <div className="file-message">
        <div className="file-thumbnail">
          {isImage && fileData.blobUrl && (
            <img 
              src={fileData.blobUrl} 
              alt={fileData.name}
              onClick={() => onMediaClick && onMediaClick(fileData.blobUrl)}
              style={{ cursor: 'pointer' }}
            />
          )}
          {isVideo && fileData.blobUrl && (
            <video 
              src={fileData.blobUrl}
              onClick={() => onMediaClick && onMediaClick(fileData.blobUrl)}
              style={{ cursor: 'pointer' }}
              onError={(e) => {
                // Fallback for unsupported formats (e.g. .avi)
                const parent = e.target.parentElement;
                if (parent) {
                  e.target.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = 'file-icon';
                  fallback.innerHTML = '<i class="fas fa-film" style="font-size:40px;color:#666"></i><div style="font-size:11px;color:#999;margin-top:4px">Format not supported in browser</div>';
                  parent.appendChild(fallback);
                }
              }}
            />
          )}
          {!isImage && !isVideo && (
            <div className="file-icon">
              <img src={getFileIcon(ext)} alt={ext} style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            </div>
          )}
        </div>
        <div className="file-info">
          <div className="file-name">{fileData.name}</div>
          <div className="file-size">{formatFileSize(fileData.size || 0)}</div>
          {fileData.blobUrl && (
            <a href={fileData.blobUrl} download={fileData.name} className="file-download-btn">
              <i className="fas fa-download" /> Download
            </a>
          )}
        </div>
      </div>
    );
  };

  // Render call event message
  const renderCallEvent = (eventData) => {
    return (
      <div className="call-event-message">
        <i className={`fas ${eventData.icon || 'fa-phone'}`} />
        <span>{eventData.text}</span>
      </div>
    );
  };

  // System messages (calls, events, etc)
  if (message.type === 'system' || message.type === 'call-event') {
    return (
      <div className="message-wrapper system-message">
        <div className="system-message-content">
          {message.type === 'call-event' ? renderCallEvent(message.data) : (
            <span>{message.data?.text || message.data}</span>
          )}
          <div className="message-time" dangerouslySetInnerHTML={{ __html: getCurrentTime() }} />
        </div>
      </div>
    );
  }

  return (
    <div className={`message-wrapper ${isOwnMessage ? 'own-message' : 'other-message'}`}>
      {!isOwnMessage && (
        <div className="message-avatar" style={{ backgroundColor: sender.color || '#666' }}>
          {initialsFrom(sender.userName || sender.initial || 'U')}
        </div>
      )}
      
      <div className="message-content">
        {!isOwnMessage && (
          <div className="message-sender">{sender.peerName || sender.userName || 'Anonymous'}</div>
        )}
        
        <div className="message-bubble">
          {message.type === 'file' ? (
            renderFileMessage(message.data)
          ) : message.type === 'link' ? (
            <>
              <div className="message-text">{formatMessageText(message.data?.text || message.data?.url)}</div>
              {renderLinkPreview(message.data)}
            </>
          ) : (
            <div className="message-text">{formatMessageText(message.data)}</div>
          )}
          
          <div className="message-time" dangerouslySetInnerHTML={{ __html: getCurrentTime() }} />
        </div>
      </div>

      {isOwnMessage && (
        <div className="message-avatar" style={{ backgroundColor: sender.color || '#0084ff' }}>
          {initialsFrom(sender.userName || sender.initial || 'Me')}
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
